import {
  Loan,
  Activity,
  LeaderboardEntry,
  ClaimedLoan,
  UrgencyLevel,
  LoanStage,
  CallOutcome,
} from './types'

const hoursAgo = (h: number) =>
  new Date(Date.now() - 1000 * 60 * 60 * h).toISOString()

const FIRST_NAMES = [
  'James',
  'Mary',
  'John',
  'Patricia',
  'Robert',
  'Jennifer',
  'Michael',
  'Linda',
  'William',
  'Elizabeth',
  'David',
  'Barbara',
  'Richard',
  'Susan',
  'Joseph',
  'Jessica',
  'Thomas',
  'Sarah',
  'Charles',
  'Karen',
  'Christopher',
  'Nancy',
  'Daniel',
  'Lisa',
  'Matthew',
  'Margaret',
  'Anthony',
  'Betty',
  'Mark',
  'Sandra',
  'Donald',
  'Ashley',
  'Steven',
  'Kimberly',
  'Paul',
  'Emily',
  'Andrew',
  'Donna',
  'Joshua',
  'Michelle',
  'Kenneth',
  'Carol',
  'Kevin',
  'Amanda',
  'Brian',
  'Melissa',
  'George',
  'Deborah',
  'Edward',
  'Stephanie',
  'Ronald',
  'Rebecca',
  'Timothy',
  'Laura',
  'Jason',
  'Sharon',
  'Jeffrey',
  'Cynthia',
  'Ryan',
  'Kathleen',
  'Jacob',
  'Amy',
  'Gary',
  'Shirley',
  'Nicholas',
  'Angela',
  'Eric',
  'Helen',
  'Jonathan',
  'Anna',
  'Stephen',
  'Brenda',
  'Larry',
  'Pamela',
  'Justin',
  'Nicole',
  'Scott',
  'Samantha',
  'Brandon',
  'Katherine',
  'Benjamin',
  'Christine',
  'Samuel',
  'Debra',
  'Frank',
  'Rachel',
  'Gregory',
  'Catherine',
]
const LAST_NAMES = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Hernandez',
  'Lopez',
  'Gonzalez',
  'Wilson',
  'Anderson',
  'Thomas',
  'Taylor',
  'Moore',
  'Jackson',
  'Martin',
  'Lee',
  'Perez',
  'Thompson',
  'White',
  'Harris',
  'Sanchez',
  'Clark',
  'Ramirez',
  'Lewis',
  'Robinson',
  'Walker',
  'Young',
  'Allen',
  'King',
  'Wright',
  'Scott',
  'Torres',
  'Nguyen',
  'Hill',
  'Flores',
  'Green',
  'Adams',
  'Nelson',
  'Baker',
  'Hall',
  'Rivera',
  'Campbell',
  'Mitchell',
  'Carter',
  'Roberts',
  'Gomez',
  'Phillips',
  'Evans',
  'Turner',
  'Diaz',
  'Parker',
  'Cruz',
  'Edwards',
  'Collins',
  'Reyes',
  'Stewart',
  'Morris',
  'Morales',
  'Murphy',
  'Cook',
  'Rogers',
  'Gutierrez',
  'Ortiz',
  'Morgan',
  'Cooper',
  'Peterson',
]
const STATES = [
  'CA',
  'TX',
  'FL',
  'NY',
  'OH',
  'WA',
  'AZ',
  'GA',
  'NC',
  'IL',
  'PA',
  'NJ',
  'VA',
  'MA',
  'CO',
  'OR',
  'MD',
  'MI',
  'TN',
  'MO',
]
const STAGES: LoanStage[] = ['Application Taken', 'Docs Out', 'Docs Signed']
const LO_POOL = [
  'Mike R.',
  'Sarah T.',
  'John D.',
  'Alex W.',
  'Jason B.',
  'Amanda C.',
  'Tom P.',
  'Chris H.',
  'Megan F.',
  'David S.',
]
const OUTCOMES: (CallOutcome | null)[] = [
  null,
  'Left Voicemail',
  'No Answer',
  'Bad Number',
  'Left Voicemail',
  'No Answer',
  null,
]

// Seeded RNG for stable mock data
function mulberry32(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0
    let t = seed
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function urgencyFor(timeSinceLastCall: number): UrgencyLevel {
  if (timeSinceLastCall > 72) return 'Critical'
  if (timeSinceLastCall > 36) return 'High'
  return 'Medium'
}

function generateBulkLoans(count: number, seed = 42): Loan[] {
  const rand = mulberry32(seed)
  const out: Loan[] = []
  for (let i = 0; i < count; i++) {
    const first = FIRST_NAMES[Math.floor(rand() * FIRST_NAMES.length)]
    const last = LAST_NAMES[Math.floor(rand() * LAST_NAMES.length)]
    const stage = STAGES[Math.floor(rand() * STAGES.length)]
    const state = STATES[Math.floor(rand() * STATES.length)]
    const lo = LO_POOL[Math.floor(rand() * LO_POOL.length)]
    const amount = Math.round((150 + rand() * 1350) * 1000)
    const timeSinceLastCall = Math.floor(rand() * 160) // 0..160 hours
    const timeInStage = Math.floor(timeSinceLastCall + rand() * 60)
    const callAttempts = Math.floor(rand() * 5)
    const lastOutcome =
      callAttempts === 0 ? null : OUTCOMES[Math.floor(rand() * OUTCOMES.length)]
    const isDialReady = lastOutcome !== 'Bad Number' && rand() > 0.15

    out.push({
      id: `L-${10000 + i}`,
      borrowerName: `${first} ${last}`,
      amount,
      stage,
      assignedLO: lo,
      timeSinceLastCall,
      timeInStage,
      state,
      urgency: urgencyFor(timeSinceLastCall),
      callAttempts,
      enteredSharkTankAt: hoursAgo(rand() * 96),
      isDialReady,
      lastCallOutcome: lastOutcome,
      lastCallAt: lastOutcome ? hoursAgo(timeSinceLastCall) : undefined,
      notes: [],
    })
  }
  return out
}

// Hand-crafted highlights at the top so the demo always shows realistic notes/outcomes
const featuredLoans: Loan[] = [
  {
    id: 'L-1001',
    borrowerName: 'Sarah Jenkins',
    amount: 450000,
    stage: 'Docs Out',
    assignedLO: 'Mike R.',
    timeSinceLastCall: 48,
    timeInStage: 84,
    state: 'CA',
    urgency: 'Critical',
    callAttempts: 0,
    enteredSharkTankAt: hoursAgo(4),
    isDialReady: true,
    lastCallOutcome: null,
    notes: [],
  },
  {
    id: 'L-1002',
    borrowerName: 'David Chen',
    amount: 625000,
    stage: 'Application Taken',
    assignedLO: 'Sarah T.',
    timeSinceLastCall: 26,
    timeInStage: 26,
    state: 'TX',
    urgency: 'High',
    callAttempts: 1,
    enteredSharkTankAt: hoursAgo(2),
    isDialReady: true,
    lastCallOutcome: 'Left Voicemail',
    lastCallAt: hoursAgo(26),
    notes: [
      {
        id: 'N-1',
        author: 'Sarah T.',
        body: 'Left detailed VM about rate lock expiring Friday. Try again EOD.',
        timestamp: hoursAgo(26),
      },
    ],
  },
  {
    id: 'L-1003',
    borrowerName: 'Marcus Johnson',
    amount: 320000,
    stage: 'Docs Signed',
    assignedLO: 'John D.',
    timeSinceLastCall: 74,
    timeInStage: 96,
    state: 'FL',
    urgency: 'Critical',
    callAttempts: 2,
    enteredSharkTankAt: hoursAgo(12),
    isDialReady: true,
    lastCallOutcome: 'No Answer',
    lastCallAt: hoursAgo(74),
    notes: [
      {
        id: 'N-2',
        author: 'John D.',
        body: 'Two no-answers in a row. Borrower mentioned travel last week.',
        timestamp: hoursAgo(74),
      },
    ],
  },
  {
    id: 'L-1006',
    borrowerName: 'Amanda Lewis',
    amount: 890000,
    stage: 'Docs Signed',
    assignedLO: 'Sarah T.',
    timeSinceLastCall: 96,
    timeInStage: 120,
    state: 'WA',
    urgency: 'Critical',
    callAttempts: 3,
    enteredSharkTankAt: hoursAgo(24),
    isDialReady: true,
    lastCallOutcome: 'No Answer',
    lastCallAt: hoursAgo(96),
    notes: [
      {
        id: 'N-4',
        author: 'Sarah T.',
        body: 'Third attempt no answer. Texting alt number listed on app.',
        timestamp: hoursAgo(96),
      },
      {
        id: 'N-5',
        author: 'Sarah T.',
        body: 'Borrower is high priority — jumbo loan ready to close.',
        timestamp: hoursAgo(120),
      },
    ],
  },
]

export const mockLoans: Loan[] = [...featuredLoans, ...generateBulkLoans(1500)]

export const mockActivities: Activity[] = [
  {
    id: 'A-1',
    loName: 'Jason B.',
    borrowerName: 'Thomas Wright',
    action: 'Funded',
    timestamp: hoursAgo(0.13),
    amount: 420000,
  },
  {
    id: 'A-2',
    loName: 'Amanda C.',
    borrowerName: 'Lisa Ray',
    action: 'Funded',
    timestamp: hoursAgo(0.58),
    amount: 550000,
  },
  {
    id: 'A-3',
    loName: 'Jason B.',
    borrowerName: 'Kevin Hart',
    action: 'Claimed',
    timestamp: hoursAgo(0.36),
    amount: 310000,
  },
]

export const mockLeaderboard: LeaderboardEntry[] = [
  {
    loName: 'Jason B.',
    points: 1840,
    fundedCount: 7,
    fundedVolume: 3120000,
    pendingCount: 2,
  },
  {
    loName: 'Amanda C.',
    points: 1605,
    fundedCount: 6,
    fundedVolume: 2750000,
    pendingCount: 1,
  },
  {
    loName: 'Mike R.',
    points: 1240,
    fundedCount: 5,
    fundedVolume: 2100000,
    pendingCount: 3,
  },
  {
    loName: 'Sarah T.',
    points: 980,
    fundedCount: 4,
    fundedVolume: 1640000,
    pendingCount: 2,
  },
  {
    loName: 'You',
    points: 0,
    fundedCount: 0,
    fundedVolume: 0,
    pendingCount: 0,
  },
  {
    loName: 'John D.',
    points: 720,
    fundedCount: 3,
    fundedVolume: 1100000,
    pendingCount: 1,
  },
  {
    loName: 'Alex W.',
    points: 540,
    fundedCount: 2,
    fundedVolume: 880000,
    pendingCount: 1,
  },
]

export const mockMyClaims: ClaimedLoan[] = [
  {
    id: 'C-2001',
    borrowerName: 'Jordan Patel',
    amount: 385000,
    stage: 'Docs Out',
    assignedLO: 'You',
    timeSinceLastCall: 6,
    timeInStage: 18,
    state: 'AZ',
    urgency: 'Medium',
    callAttempts: 2,
    enteredSharkTankAt: hoursAgo(36),
    isDialReady: true,
    lastCallOutcome: 'Connected',
    lastCallAt: hoursAgo(6),
    claimedAt: hoursAgo(5),
    status: 'pending',
    notes: [
      {
        id: 'N-100',
        author: 'You',
        body: 'Spoke with borrower — confirmed income docs incoming Tuesday.',
        timestamp: hoursAgo(6),
      },
    ],
  },
  {
    id: 'C-2002',
    borrowerName: 'Nicole Adams',
    amount: 540000,
    stage: 'Processing',
    assignedLO: 'You',
    timeSinceLastCall: 2,
    timeInStage: 4,
    state: 'CA',
    urgency: 'Medium',
    callAttempts: 4,
    enteredSharkTankAt: hoursAgo(72),
    isDialReady: false,
    lastCallOutcome: 'Left Voicemail',
    lastCallAt: hoursAgo(2),
    claimedAt: hoursAgo(26),
    status: 'pending',
    notes: [
      {
        id: 'N-101',
        author: 'You',
        body: 'VM about appraisal scheduling. Will follow up tomorrow AM.',
        timestamp: hoursAgo(2),
      },
      {
        id: 'N-102',
        author: 'You',
        body: 'Co-borrower prefers email — sent docs link.',
        timestamp: hoursAgo(20),
      },
    ],
  },
]
